import { bridge } from '../../bridge'
import type { TranscriptEntry } from '../../store/types'

// The decision logic behind a hands-free voice session, kept out of the
// component so it can be tested without a DOM.
//
// The session itself is deliberately thin: it does not record, and it does not
// detect turns. Those arrive in step 4. What it does is make the phone SPEAK
// what the agent says, and mark the messages you send as spoken so the agent
// knows to answer aloud. See docs/phone-voice-endpoint-plan.md §7 step 3.

export type VoiceCapabilities = {
  speak: boolean
  session: boolean
  onDeviceRecognition: boolean
  reason: string | null
}

/**
 * Ask the shell what it can do.
 *
 * Returns null when the shell predates `voice.*` entirely — the call rejects
 * with `bridge.unknown_method`, and that rejection IS the feature probe. The web
 * app is served remotely while native shells lag by weeks, so "new web, old
 * shell" is the default state after every deploy rather than an edge case. Never
 * substitute a version comparison: `app.info`'s version only moves when someone
 * bumps it, which is not the same question.
 */
export async function probeVoiceCapabilities(): Promise<VoiceCapabilities | null> {
  try {
    return await bridge.call('voice.capabilities')
  } catch {
    return null
  }
}

/** Whether a voice session should be offered at all on this shell. */
export function voiceSessionOffered(caps: VoiceCapabilities | null): boolean {
  // `speak` alone. A shell that can talk is already useful even without
  // hands-free listening, and gating the whole feature on `session` would hide
  // it from every phone that has not shipped turn detection yet.
  return caps?.speak === true
}

/**
 * Whether the session should listen hands-free rather than waiting for mic taps.
 *
 * Requires BOTH: a shell that can detect turns, and recognition we can prove
 * stays on the device. The second is not a nicety — continuously capturing a
 * room is a different promise from a tapped mic button, and §3.6 is explicit
 * that hands-free is gated on it while the button is not.
 *
 * Where this is false the session still works, just tap-to-talk. That is the
 * graceful degradation the capability probe exists for: an older Android phone
 * gets spoken replies without a live microphone.
 */
export function handsFreeAvailable(caps: VoiceCapabilities | null): boolean {
  return caps?.session === true && caps?.onDeviceRecognition === true
}

/**
 * Ids of transcript entries that carry spoken words.
 *
 * Used to seed a session's "already said" set at the moment it starts. Without
 * this, opening voice mode on an agent you have been chatting with all morning
 * would read the entire morning back to you.
 */
export function spokenEntryIDs(entries: TranscriptEntry[]): Set<string> {
  const ids = new Set<string>()
  for (const e of entries) {
    if (e.voice?.spoken) ids.add(e.id)
  }
  return ids
}

/**
 * The entries to speak next, oldest first: those carrying spoken words that the
 * session has not already voiced.
 *
 * Returns every unspoken entry rather than just the newest, so a burst that
 * arrives between renders is not silently dropped — the caller decides what to
 * do with more than one (today: speak the last, since `voice.speak` interrupts
 * anyway and reading a backlog aloud helps nobody).
 */
export function unspokenEntries(
  entries: TranscriptEntry[],
  alreadySpoken: Set<string>,
): TranscriptEntry[] {
  return entries.filter((e) => e.voice?.spoken && !alreadySpoken.has(e.id))
}

/** Who is being asked to approve a parked action. */
export type ApprovalAudience = 'you' | 'household'

/**
 * What a voice session says when the agent hits an action needing approval.
 *
 * Without this the session simply goes quiet on someone who just asked for
 * something out loud — the same failure the puck's `pendingApprovalSpeech`
 * exists to prevent, and the reason the relay learned to speak outcomes.
 *
 * Two audiences, because getting this wrong is not merely vague but FALSE. A
 * phone carries a human principal, so its speaker is frequently in the approver
 * set — and when they are, the approval card is about to appear on the very
 * device that just spoke. Telling that person the household is being consulted
 * promises someone else is deciding, and a modal immediately contradicts it.
 *
 * Both lines stay discreet, for the same reason the puck's does: the premise of
 * this feature is a phone lying face-up on a table, which is a room speaker. So
 * neither names a tool, a target, or who decided.
 * See docs/phone-voice-endpoint-plan.md §3.7.
 */
export function approvalPendingSpeech(audience: ApprovalAudience): string {
  return audience === 'you'
    ? 'That needs your approval — it’s on your screen.'
    : 'I’ll need to check with the household before I can do that. I’ve asked — hang tight.'
}
